import { useMemo } from "react";
import { useHorizontalWheelScroll } from "../../hooks/useHorizontalWheelScroll";
import { useLocalization } from "../../hooks/useLocalization";
import { useLocalePath } from "../../hooks/useLocalePath";
import { IPlayerSeasonStats } from "../../types/types";
import { getPlayerProfilePath } from "../../utils/playerProfilePath";
import {
  assistsPerGameTier,
  csPerMinTier,
  deathsPerGameTier,
  dpmTier,
  kdaTier,
  killsPerGameTier,
  kpPctTier,
} from "../../utils/playerStatTiers";
import {
  formatCountStat,
  formatStat,
  hasSeasonMultikillStats,
  normalizeSeasonStatsRole,
  type SeasonStatsSortKey,
  type SortDirection,
} from "../../utils/seasonstats/table";
import { buildRelativeCountTierFn } from "../../utils/seasonstats/multikillTiers";
import { PlayerRoleBadge } from "../leaderboard/PlayerRoleBadge";
import { StatValue } from "../shared/StatValue";
import { TeamLogo } from "../shared/TeamLogo";

interface SeasonStatsTableProps {
  rows: IPlayerSeasonStats[];
  teamNames: Record<string, string>;
  sortKey: SeasonStatsSortKey;
  sortDirection: SortDirection;
  onSort: (key: SeasonStatsSortKey) => void;
}

export const SeasonStatsTable = ({
  rows,
  teamNames,
  sortKey,
  sortDirection,
  onSort,
}: SeasonStatsTableProps) => {
  const { strings } = useLocalization();
  const { goTo } = useLocalePath();
  const copy = strings.SEASON_STATS;
  const showMultikillColumns = hasSeasonMultikillStats(rows);
  const doubleKillTier = useMemo(
    () => buildRelativeCountTierFn(rows, (row) => row.doubleKills),
    [rows],
  );
  const tripleKillTier = useMemo(
    () => buildRelativeCountTierFn(rows, (row) => row.tripleKills),
    [rows],
  );
  const quadraKillTier = useMemo(
    () => buildRelativeCountTierFn(rows, (row) => row.quadraKills),
    [rows],
  );
  const pentaKillTier = useMemo(
    () => buildRelativeCountTierFn(rows, (row) => row.pentaKills),
    [rows],
  );
  const tableWrapRef = useHorizontalWheelScroll<HTMLDivElement>();

  const multikillColumns: {
    key: SeasonStatsSortKey;
    label: string;
    className?: string;
  }[] = showMultikillColumns
    ? [
        { key: "doubleKills", label: copy.COLUMNS.DOUBLE, className: "season-stats-col-multikill" },
        { key: "tripleKills", label: copy.COLUMNS.TRIPLE, className: "season-stats-col-multikill" },
        { key: "quadraKills", label: copy.COLUMNS.QUADRA, className: "season-stats-col-multikill" },
        { key: "pentaKills", label: copy.COLUMNS.PENTA, className: "season-stats-col-multikill" },
      ]
    : [];

  const columns: {
    key: SeasonStatsSortKey | null;
    label: string;
    className?: string;
  }[] = [
    { key: null, label: "#", className: "season-stats-col-rank" },
    { key: "playerName", label: copy.COLUMNS.PLAYER, className: "season-stats-col-player" },
    { key: "teamId", label: copy.COLUMNS.TEAM, className: "season-stats-col-team" },
    { key: "role", label: copy.COLUMNS.ROLE, className: "season-stats-col-role" },
    { key: "kda", label: "KDA", className: "season-stats-col-stat" },
    { key: "kills", label: "K", className: "season-stats-col-stat" },
    { key: "deaths", label: "D", className: "season-stats-col-stat" },
    { key: "assists", label: "A", className: "season-stats-col-stat" },
    ...multikillColumns,
    { key: "csPerMin", label: "CS/m", className: "season-stats-col-stat" },
    { key: "kpPct", label: "KP%", className: "season-stats-col-stat" },
    { key: "dpm", label: "DPM", className: "season-stats-col-stat" },
    { key: "gamesPlayed", label: copy.COLUMNS.GAMES, className: "season-stats-col-stat" },
    { key: "champsPlayed", label: copy.COLUMNS.CHAMPS, className: "season-stats-col-stat" },
  ];

  if (!rows.length) {
    return <div className="season-stats-empty">{copy.NO_RESULTS}</div>;
  }

  return (
    <div
      ref={tableWrapRef}
      className="w-full overflow-x-auto overscroll-x-contain [-webkit-overflow-scrolling:touch]"
    >
      <div
        className={showMultikillColumns ? "season-stats-table--multikills" : "season-stats-table"}
      >
        <div className="grid grid-cols-subgrid col-span-full min-h-[2.75rem] w-full items-center gap-x-[0.85rem] gap-y-[0.65rem] border-b border-b-[var(--surface-card-border)] px-5 text-[0.72rem] font-semibold tracking-[0.03em] text-[#8b8b9a] uppercase">
          {columns.map((column) => {
            if (!column.key) {
              return (
                <span key={column.label} className={column.className}>
                  {column.label}
                </span>
              );
            }

            const active = sortKey === column.key;
            const indicator = active ? (sortDirection === "asc" ? "↑" : "↓") : "";

            return (
              <button
                key={column.key}
                type="button"
                className={`fh-ghost-btn season-stats-sort${active ? " season-stats-sort--active" : ""} ${column.className ?? ""}`}
                onClick={() => {
                  onSort(column.key as SeasonStatsSortKey);
                }}
              >
                <span className="min-w-0">{column.label}</span>
                <span
                  className={`w-3 shrink-0 text-center${active ? "" : " invisible"}`}
                  aria-hidden={!active}
                >
                  {indicator}
                </span>
              </button>
            );
          })}
        </div>

        {rows.map((row, index) => {
          const role = normalizeSeasonStatsRole(row.role);
          const teamName = teamNames[row.teamId] ?? row.teamId;

          return (
            <div
              className="season-stats-row-link grid grid-cols-subgrid col-span-full min-h-[3.25rem] w-full cursor-pointer items-center gap-x-[0.85rem] gap-y-[0.65rem] border-b border-b-[var(--surface-card-border)] bg-[var(--surface-card-row-bg)] px-5 text-[0.92rem] text-inherit no-underline outline-none transition-[background-color,box-shadow] duration-150 ease-in-out"
              key={`${row.teamId}-${row.playerName}`}
              role="link"
              tabIndex={0}
              onClick={(event) => {
                if ((event.target as HTMLElement).closest("a")) return;
                goTo(getPlayerProfilePath(row.playerName));
              }}
              onKeyDown={(event) => {
                if (event.key !== "Enter" && event.key !== " ") return;
                if ((event.target as HTMLElement).closest("a")) return;
                event.preventDefault();
                goTo(getPlayerProfilePath(row.playerName));
              }}
            >
              <span className="season-stats-col-rank">{index + 1}</span>
              <div className="season-stats-col-player">
                <span className="season-stats-player-name font-semibold text-[#e8e8ef]">
                  {row.playerName}
                </span>
              </div>
              <div className="season-stats-col-team">
                <TeamLogo
                  teamId={row.teamId}
                  alt={teamName}
                  className="h-5 w-5 shrink-0 object-contain"
                />
                <span title={teamName} className="overflow-hidden text-ellipsis whitespace-nowrap">
                  {teamName}
                </span>
              </div>
              <div className="season-stats-col-role">
                {role ? <PlayerRoleBadge role={role} small /> : <span>—</span>}
              </div>
              <StatValue
                className="season-stats-col-stat"
                value={row.kda}
                formatted={formatStat(row.kda, 2)}
                tier={kdaTier(row.kda)}
              />
              <StatValue
                className="season-stats-col-stat"
                value={row.kills}
                formatted={formatStat(row.kills, 1)}
                tier={killsPerGameTier(row.kills)}
              />
              <StatValue
                className="season-stats-col-stat"
                value={row.deaths}
                formatted={formatStat(row.deaths, 1)}
                tier={deathsPerGameTier(row.deaths)}
              />
              <StatValue
                className="season-stats-col-stat"
                value={row.assists}
                formatted={formatStat(row.assists, 1)}
                tier={assistsPerGameTier(row.assists)}
              />
              {showMultikillColumns ? (
                <>
                  <StatValue
                    className="season-stats-col-multikill"
                    value={row.doubleKills ?? 0}
                    formatted={formatCountStat(row.doubleKills)}
                    tier={doubleKillTier(row.doubleKills)}
                  />
                  <StatValue
                    className="season-stats-col-multikill"
                    value={row.tripleKills ?? 0}
                    formatted={formatCountStat(row.tripleKills)}
                    tier={tripleKillTier(row.tripleKills)}
                  />
                  <StatValue
                    className="season-stats-col-multikill"
                    value={row.quadraKills ?? 0}
                    formatted={formatCountStat(row.quadraKills)}
                    tier={quadraKillTier(row.quadraKills)}
                  />
                  <StatValue
                    className="season-stats-col-multikill"
                    value={row.pentaKills ?? 0}
                    formatted={formatCountStat(row.pentaKills)}
                    tier={pentaKillTier(row.pentaKills)}
                  />
                </>
              ) : null}
              <StatValue
                className="season-stats-col-stat"
                value={row.csPerMin}
                formatted={formatStat(row.csPerMin, 2)}
                tier={csPerMinTier(row.csPerMin)}
              />
              <StatValue
                className="season-stats-col-stat"
                value={row.kpPct}
                formatted={formatStat(row.kpPct, 1)}
                tier={kpPctTier(row.kpPct)}
              />
              <StatValue
                className="season-stats-col-stat"
                value={row.dpm}
                formatted={formatStat(row.dpm, 0)}
                tier={dpmTier(row.dpm)}
              />
              <StatValue
                className="season-stats-col-stat"
                value={row.gamesPlayed}
                formatted={formatStat(row.gamesPlayed, 0)}
                tier="neutral"
              />
              <StatValue
                className="season-stats-col-stat"
                value={row.champsPlayed}
                formatted={formatStat(row.champsPlayed, 0)}
                tier="neutral"
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};
